/**
 * The figures in a piece of text: money, percentages, durations, dates and
 * years — the things a policy gets wrong most expensively.
 *
 * Used to check what a writing model hands back. It is told never to invent
 * a number, but a rule in a prompt is a request, not a guarantee, so the
 * dashboard compares the suggestion with what it was given and points at
 * anything new.
 */
/** 1200, 1,200 or 1,200.50 — but not the comma that merely ends a clause. */
const N = String.raw `\d+(?:,\d{3})*(?:\.\d+)?`;
const FIGURE = new RegExp([
    // GHS 50, GH₵50, RWF 1,200, $10, €5.50
    `(?:GH[S₵]|RWF|FRW|USD|EUR|GBP|[$€£])\\s?${N}`,
    // 50 GHS, 1,200 RWF
    `${N}\\s?(?:GHS|GH₵|RWF|FRW|USD|EUR|GBP)\\b`,
    // 15%, 15 percent, 24 hours, 3 days, 100 km
    `${N}\\s?(?:%|percent\\b|(?:minute|hour|day|week|month|year|km|kilometre|mile)s?\\b)`,
    // 06/10/2026, 6-10-26, 6.10.2026
    String.raw `\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b`,
    // 6 October 2026, October 6, 6th of October
    String.raw `\b\d{1,2}(?:st|nd|rd|th)?\s(?:of\s)?(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?(?:\s\d{4})?`,
    String.raw `\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s\d{1,2}(?:st|nd|rd|th)?(?:,?\s\d{4})?`,
    // 2026
    String.raw `\b(?:19|20)\d{2}\b`,
].join('|'), 'gi');
const normalise = (figure) => figure.replace(/[\s,]+/g, '').toLowerCase();
export function findFigures(text) {
    return [...text.matchAll(FIGURE)].map((match) => match[0].trim());
}
/**
 * The figures in `after` that `before` did not already contain, in the order
 * they appear and without repeats. Spacing and thousands separators do not
 * make a figure new: "GHS 1,200" and "GHS1200" are the same one.
 */
export function newFigures(before, after) {
    const known = new Set(findFigures(before).map(normalise));
    const seen = new Set();
    const out = [];
    for (const figure of findFigures(after)) {
        const key = normalise(figure);
        if (known.has(key) || seen.has(key))
            continue;
        seen.add(key);
        out.push(figure);
    }
    return out;
}
